import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { copyText, dateTime } from '@/lib/utils';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { apiMessage } from './LabBenchErrors';

interface ImportAgent {
  id: string;
  code: string;
  name: string;
  keyPrefix: string;
  isActive: boolean;
  lastSeenAt: string | null;
  lastMessageAt: string | null;
  presenceNote: string | null;
}

interface RadiologySettings {
  radiologySeparateVerifier: boolean | null;
  effective: boolean;
  labSeparateVerifier: boolean;
}

const AGENTS_KEY = ['radiology-import-agents'];

export function RadiologySetup() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const mayManageAgents = can('radiology.import.manage');
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [revealed, setRevealed] = useState<{ name: string; key: string } | null>(null);

  const agents = useQuery({
    queryKey: AGENTS_KEY,
    queryFn: async () => (await get<{ data: ImportAgent[] }>('/radiology/import-agents?limit=100')).data,
    enabled: can('radiology.import.manage', 'radiology.master.manage'),
  });
  const settings = useQuery({
    queryKey: ['radiology-settings'],
    queryFn: () => get<RadiologySettings>('/radiology/settings'),
    enabled: can('radiology.master.manage', 'radiology.report.verify'),
  });

  const create = useMutation({
    mutationFn: () => post<ImportAgent & { key: string }>('/radiology/import-agents', { code: code.trim().toUpperCase(), name: name.trim() }),
    onSuccess: (agent) => {
      setAdding(false);
      setCode('');
      setName('');
      setRevealed({ name: agent.name, key: agent.key });
      void queryClient.invalidateQueries({ queryKey: AGENTS_KEY });
    },
  });
  const rotate = useMutation({
    mutationFn: (agent: ImportAgent) => post<ImportAgent & { key: string }>(`/radiology/import-agents/${agent.id}/rotate-key`, {}),
    onSuccess: (agent) => setRevealed({ name: agent.name, key: agent.key }),
    onError: (error) => toast.error(tr('radiology', 'could_not_issue_a_new_key'), apiMessage(error)),
  });
  const toggle = useMutation({
    mutationFn: (agent: ImportAgent) => put(`/radiology/import-agents/${agent.id}`, { isActive: !agent.isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: AGENTS_KEY }),
    onError: (error) => toast.error(tr('radiology', 'could_not_change_the_agent'), apiMessage(error)),
  });
  const saveSettings = useMutation({
    mutationFn: (value: boolean | null) => put<RadiologySettings>('/radiology/settings', { radiologySeparateVerifier: value }),
    onSuccess: () => {
      toast.success(tr('radiology', 'radiology_settings_saved'));
      void queryClient.invalidateQueries({ queryKey: ['radiology-settings'] });
    },
    onError: (error) => toast.error(tr('radiology', 'could_not_save_the_settings'), apiMessage(error)),
  });

  const verifierChoice = settings.data?.radiologySeparateVerifier === null || settings.data === undefined ? '' : settings.data.radiologySeparateVerifier ? 'on' : 'off';

  return (
    <div className="grid gap-4">
      {settings.data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('radiology', 'verification')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Field
              label={tr('radiology', 'the_radiologist_who_wrote_a_report_cannot')}
              hint={`Now ${settings.data.effective ? 'on' : 'off'}${settings.data.radiologySeparateVerifier === null ? ', following the laboratory setting' : ''}`}
            >
              <ChoiceButtons<'' | 'on' | 'off'>
                inline
                value={verifierChoice}
                disabled={!can('radiology.master.manage') || saveSettings.isPending}
                onChange={(value) => {
                  if (value !== verifierChoice) saveSettings.mutate(value === '' ? null : value === 'on');
                }}
                options={[
                  { value: '', label: `${tr('radiology', 'same_as_the_laboratory')}${settings.data.labSeparateVerifier ? tr('radiology', 'on') : tr('radiology', 'off')})` },
                  { value: 'on', label: tr('radiology', 'on_2') },
                  { value: 'off', label: tr('radiology', 'off_2') },
                ]}
              />
            </Field>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">{tr('radiology', 'image_import_agents')}</CardTitle>
          {mayManageAgents && (
            <Button size="sm" onClick={() => setAdding(true)}>
              {tr('radiology', 'add_agent')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <DataTable
            rows={agents.data}
            loading={agents.isLoading} refreshing={agents.isFetching}
            error={agents.error}
            rowKey={(row) => row.id}
            empty={tr('radiology', 'no_import_agent_yet_add_one_for_each_modality')}
            columns={[
              {
                header: tr('radiology', 'agent'),
                cell: (row) => (
                  <div>
                    <p className="font-medium">{row.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {row.code} · {row.keyPrefix}…
                    </p>
                  </div>
                ),
              },
              {
                header: tr('radiology', 'last_image'),
                cell: (row) => <span className="text-xs">{row.presenceNote ?? (row.lastMessageAt ? dateTime(row.lastMessageAt) : tr('radiology', 'never'))}</span>,
              },
              { header: tr('radiology', 'status'), cell: (row) => <Badge variant={row.isActive ? 'success' : 'muted'}>{row.isActive ? tr('radiology', 'active') : tr('radiology', 'disabled')}</Badge> },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  mayManageAgents ? (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => rotate.mutate(row)} loading={rotate.isPending && rotate.variables?.id === row.id}>
                        {tr('radiology', 'new_key')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggle.mutate(row)}>
                        {row.isActive ? tr('radiology', 'disable') : tr('radiology', 'enable')}
                      </Button>
                    </div>
                  ) : null,
              },
            ]}
          />
        </CardContent>
      </Card>

      {adding && (
        <Dialog
          open
          onClose={() => setAdding(false)}
          title={tr('radiology', 'add_an_image_import_agent')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setAdding(false)}>
                {tr('radiology', 'cancel')}
              </Button>
              <Button onClick={() => create.mutate()} loading={create.isPending} disabled={code.trim().length < 2 || name.trim().length < 2}>
                {tr('radiology', 'add')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            {create.error && <Alert variant="destructive">{apiMessage(create.error)}</Alert>}
            <Field label={tr('radiology', 'code')} hint={tr('radiology', 'letters_digits_and')}>
              <Input value={code} maxLength={24} onChange={(e) => setCode(e.target.value.toUpperCase())} />
            </Field>
            <Field label={tr('radiology', 'name')}>
              <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}
      {revealed && (
        <Dialog
          open
          onClose={() => setRevealed(null)}
          title={tr('radiology', 'key_for_x', { name: revealed.name })}
          description={tr('radiology', 'shown_once_put_it_in_the_agents_settings_on_the')}
          footer={
            <>
              <Button variant="outline" onClick={() => void copyText(revealed.key).then(() => toast.success(tr('radiology', 'key_copied')))}>
                {tr('radiology', 'copy')}
              </Button>
              <Button onClick={() => setRevealed(null)}>{tr('radiology', 'done')}</Button>
            </>
          }
        >
          <p className="break-all rounded-md bg-muted p-3 font-mono text-sm">{revealed.key}</p>
        </Dialog>
      )}
    </div>
  );
}
